import { useCallback, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { JUNGLE_SUPPORT_UI_IS_ENABLED } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { useCustomerSupportChatStore } from "../../zustand/useCustomerSupportChatStore";

/**
 * "get help / give feedback": opens the floating support panel on its feedback options, the entry point people
 * used to see on `/give-feedback` (which is where it goes while the support UI is turned off). Pressed again while
 * the panel is open, it closes it (owner, 2026-10-29). Reads the store only when pressed, so opening or closing the
 * panel never re-renders the caller.
 */
export function useOpenCustomerSupportFeedbackOptions(): () => void {
  const navigate = useNavigate();
  const openCustomerSupportChatbotPanel = useCustomerSupportChatStore((state) => state.openPanel);
  const closeCustomerSupportChatbotPanel = useCustomerSupportChatStore((state) => state.closePanel);
  const setCustomerSupportChatbotPanelView = useCustomerSupportChatStore((state) => state.setCurrentView);
  return useCallback(() => {
    if (!JUNGLE_SUPPORT_UI_IS_ENABLED) {
      navigate("/give-feedback");
      return;
    }
    const { isPanelOpen, isPanelTemporarilyHiddenForEvidenceCapture } = useCustomerSupportChatStore.getState();
    // Hidden for a screen recording, the panel is still logically open but not on screen: show it, don't close it.
    if (isPanelOpen && !isPanelTemporarilyHiddenForEvidenceCapture) {
      closeCustomerSupportChatbotPanel();
      return;
    }
    setCustomerSupportChatbotPanelView("feedback_options");
    openCustomerSupportChatbotPanel();
  }, [navigate, openCustomerSupportChatbotPanel, closeCustomerSupportChatbotPanel, setCustomerSupportChatbotPanelView]);
}

/** A "get help / give feedback" toggle's `aria-expanded`: whether the support panel is on screen (undefined while the
 * support UI is off and the toggle is a link to `/give-feedback`). Only the toggle inside re-renders when it changes. */
export function CustomerSupportPanelExpanded({ children }: { children: (expanded: boolean | undefined) => ReactElement }): ReactElement {
  const shown = useCustomerSupportChatStore((state) => state.isPanelOpen && !state.isPanelTemporarilyHiddenForEvidenceCapture);
  return children(JUNGLE_SUPPORT_UI_IS_ENABLED ? shown : undefined);
}
